import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import Loading from '../components/Common/Loading';
import { useDeviceAuth } from '../context/DeviceAuthContext';
import { useLocalVideos } from '../hooks/useLocalVideos'; // Import to get video titles
import api, { getConfiguration, createTransaction, validatePromo } from '../services/api';
import '../styles/CheckoutPage.css';
import { Video } from '../types/video';
import EmailInput from '../components/EmailInput';
import { uploadVideosToGoogleDrive } from '../utils/zipUtils';
import imageConfig from '../config/images';

// Update the TypeScript declaration for snap to include embed function
declare global {
  interface Window {
    snap: {
      pay: (token: string, options: {
        onSuccess: (result: any) => void;
        onPending: (result: any) => void;
        onError: (result: any) => void;
        onClose: () => void;
      }) => void;
      embed: (token: string, options: {
        embedId: string;
        onSuccess?: (result: any) => void;
        onPending?: (result: any) => void;
        onError?: (result: any) => void;
        onClose?: () => void;
      }) => void;
    };
  }
}

// Update the loadSnapScript function to include the client key
const loadSnapScript = (clientKey: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if script is already loaded
    if (window.snap) {
      resolve();
      return;
    }

    const snapUrl = process.env.REACT_APP_MIDTRANS_SNAP_JS_SOURCE;
    if (!snapUrl) {
      console.error('REACT_APP_MIDTRANS_SNAP_JS_SOURCE is not set in environment variables');
      reject(new Error('Payment system URL is not properly configured'));
      return;
    }

    const script = document.createElement('script');
    script.src = snapUrl;
    script.setAttribute('data-client-key', clientKey);
    script.onload = () => resolve();
    script.onerror = (error) => reject(error);
    document.head.appendChild(script);
  });
};

// Add isStaging to the component props
interface CheckoutPageProps {
  isStaging?: boolean;
}

// Update the bundlingPromo state and interface
interface BundlingPromoItem {
  QUANTITY: number;
  PRICE: number;
  ENABLED: boolean;
}

const CheckoutPage: React.FC<CheckoutPageProps> = ({ isStaging = false }) => {
  const [email, setEmail] = useState('');
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number>(15);
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { selectedVideos, clearCart } = useCart();
  const history = useHistory();
  const location = useLocation();
  const { deviceId, isDeviceAuthorized } = useDeviceAuth();
  const { videos, cancelBackgroundTasks, getVideoFiles } = useLocalVideos();
  const [venueId, setVenueId] = useState<number | null>(null);
  const [fieldId, setFieldId] = useState<number | null>(null);
  const [defaultPrice, setDefaultPrice] = useState<number>(1000);
  const [bundlingPromos, setBundlingPromos] = useState<BundlingPromoItem[]>([]);
  const [paymentGatewayEnabled, setPaymentGatewayEnabled] = useState<boolean>(true);
  const [effectiveVideoDetails, setEffectiveVideoDetails] = useState<Video[]>([]);
  const [zipStatus, setZipStatus] = useState<string>('');
  const [zipProgress, setZipProgress] = useState<number>(0);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState<string>('');
  const [promoError, setPromoError] = useState<string | null>(null);
  const [validatedPromo, setValidatedPromo] = useState<{
    id: number;
    name: string;
    code: string;
    amount: number;
    percent: number;
  } | null>(null);
  const [isValidatingPromo, setIsValidatingPromo] = useState<boolean>(false);
  const [discountAmount, setDiscountAmount] = useState<number>(0);

  // In the component, add a state to control visibility of payment form
  const [showPaymentForm, setShowPaymentForm] = useState<boolean>(false);

  // Load price and bundling promo from configuration
  useEffect(() => {
    const loadConfigurations = async () => {
      try {
        // Use the appropriate configuration keys based on isStaging
        const priceConfigKey = isStaging ? 'DEFAULT_PRICE_STAGING' : 'DEFAULT_PRICE';
        const bundlingConfigKey = isStaging ? 'PROMO_TABLE_STAGING' : 'PROMO_TABLE';
        const gatewayConfigKey = isStaging ? 'ENABLE_PAYMENT_GATEWAY_STAGING' : 'ENABLE_PAYMENT_GATEWAY';
        
        // Load price
        const price = await getConfiguration(priceConfigKey, '1000');
        const priceValue = parseInt(price, 10);
        if (!isNaN(priceValue)) {
          setDefaultPrice(priceValue);
        }
        
        // Load bundling promo
        const bundlingPromoStr = await getConfiguration(bundlingConfigKey, '');
        if (bundlingPromoStr) {
          try {
            let bundlingValue = JSON.parse(bundlingPromoStr);
            
            // Handle both array and single object formats
            if (!Array.isArray(bundlingValue)) {
              if (bundlingValue && typeof bundlingValue === 'object') {
                bundlingValue = [{ ...bundlingValue, ENABLED: true }];
              } else {
                bundlingValue = [];
              }
            }
            
            setBundlingPromos(bundlingValue);
          } catch (err) {
            console.error('Failed to parse bundling promo:', err);
          }
        }
        
        // Load payment gateway status
        const gatewayEnabled = await getConfiguration(gatewayConfigKey, 'true');
        setPaymentGatewayEnabled(gatewayEnabled === 'true');
        
      } catch (error) {
        console.error('Error loading configurations:', error);
        // Keep the default values
      }
    };
    loadConfigurations();
  }, [isStaging]);
  
  // Use a ref to track if we've loaded videos
  const hasProcessedVideosRef = useRef(false);
  
  // Try to get selected videos from localStorage first to ensure consistency
  useEffect(() => {
    // Skip if we've already processed videos or if no selected videos
    if (hasProcessedVideosRef.current) return;
    
    // First check if we have selected video IDs in localStorage
    // This ensures we use the exact same videos that were selected in VideoSelectionPage
    try {
      const storedSelectedIds = localStorage.getItem('clipo-selected-videos');
      if (storedSelectedIds) {
        const parsedSelectedIds = JSON.parse(storedSelectedIds) as string[];
        
        if (parsedSelectedIds.length > 0) {
          console.log('Using selected videos from localStorage:', parsedSelectedIds);
          
          // Try to get video details from the videos hook
          const foundVideos = videos.filter(video => parsedSelectedIds.includes(video.id));
          
          if (foundVideos.length === parsedSelectedIds.length) {
            // We found all videos in the hook, use those
            setEffectiveVideoDetails(foundVideos);
            hasProcessedVideosRef.current = true;
          } else {
            // If we didn't find all videos in the hook, try localStorage cache
            const cachedVideosString = localStorage.getItem('clipo-video-cache');
            if (cachedVideosString) {
              const cachedVideos = JSON.parse(cachedVideosString) as Video[];
              
              // Filter to just the selected ones, ensuring order matches the selected IDs
              const localStorageFallbackVideos = parsedSelectedIds
                .map(id => cachedVideos.find((video: Video) => video.id === id))
                .filter((video): video is Video => video !== undefined);
              
              if (localStorageFallbackVideos.length === parsedSelectedIds.length) {
                setEffectiveVideoDetails(localStorageFallbackVideos);
                hasProcessedVideosRef.current = true;
              } else if (localStorageFallbackVideos.length > 0) {
                // If we found some but not all, use what we found
                setEffectiveVideoDetails(localStorageFallbackVideos);
                hasProcessedVideosRef.current = true;
                console.warn(`Only found ${localStorageFallbackVideos.length} out of ${parsedSelectedIds.length} selected videos`);
              }
            }
          }
        }
      }
    } catch (err) {
      console.error("Error restoring selected videos from localStorage:", err);
    }
    
    // Fallback to using selectedVideos from cart context if localStorage approach failed
    if (!hasProcessedVideosRef.current && selectedVideos.length > 0) {
      const foundVideos = videos.filter(video => selectedVideos.includes(video.id));
      
      if (foundVideos.length > 0) {
        // If we found videos in the hook, use those
        setEffectiveVideoDetails(foundVideos);
        hasProcessedVideosRef.current = true;
      } else {
        // If no videos found from hook, try localStorage as last resort
        try {
          const cachedVideosString = localStorage.getItem('clipo-video-cache');
          if (cachedVideosString) {
            const cachedVideos = JSON.parse(cachedVideosString) as Video[];
            
            // Filter to just the selected ones, preserving order
            const localStorageFallbackVideos = selectedVideos
              .map(id => cachedVideos.find((video: Video) => video.id === id))
              .filter((video): video is Video => video !== undefined);
            
            if (localStorageFallbackVideos.length > 0) {
              setEffectiveVideoDetails(localStorageFallbackVideos);
              hasProcessedVideosRef.current = true;
            }
          }
        } catch (err) {
          console.error("Error restoring videos from localStorage:", err);
        }
      }
    }
  }, [videos, selectedVideos]);

  useEffect(() => {
    const getVenueFromDevice = async () => {
      if (deviceId) {
        try {
          // Use the correct endpoint to get device details with venue info and field info
          const response = await api.get(`/device/${deviceId}`);
          if (response.data && response.data.venueId) {
            setVenueId(response.data.venueId);
          }
          if (response.data && response.data.fieldId) {
            setFieldId(response.data.fieldId);
          }
        } catch (err) {
          console.error('Could not fetch venue and field for device:', err);
        }
      }
    };
    
    getVenueFromDevice();
  }, [deviceId]);

  // Redirect if not authorized or no videos selected
  useEffect(() => {
    if (!isDeviceAuthorized || (selectedVideos.length === 0 && effectiveVideoDetails.length === 0)) {
      history.replace('/box/video-selection');
    }
  }, [isDeviceAuthorized, selectedVideos, effectiveVideoDetails, history]);

  // Handle countdown and redirect after successful purchase
  useEffect(() => {
    // Only start countdown if there's a success message
    if (successMessage && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      
      return () => clearTimeout(timer);
    } else if (successMessage && countdown === 0) {
      // Clear any existing timeout
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
      
      // Clear cart and redirect to home page
      clearCart();
      history.replace('/box'); // Redirect to the new /box path
    }
  }, [successMessage, countdown, clearCart, history]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  // Update email handling to prevent setState loops
  const handleEmailChange = useCallback((newEmail: string) => {
    setEmail(newEmail);
    // Clear any error when user types
    if (error) setError(null);
  }, [error]);

  // Calculate item price based on bundling promo
  const calculateItemPrice = useCallback((index: number): number => {
    // If payment gateway is disabled or price is 0, all items are free
    if (!paymentGatewayEnabled || defaultPrice === 0) {
      return 0;
    }
    
    // If no bundling promos, use default price
    if (!bundlingPromos || bundlingPromos.length === 0) {
      return defaultPrice;
    }
    
    // Find the highest applicable bundling tier for the total video count
    const enabledPromos = bundlingPromos
      .filter((promo: BundlingPromoItem) => promo.ENABLED && promo.QUANTITY > 0 && promo.PRICE >= 0)
      .sort((a: BundlingPromoItem, b: BundlingPromoItem) => b.QUANTITY - a.QUANTITY);
    
    // Find the applicable promo for the total video count
    const totalVideoCount = effectiveVideoDetails.length;
    const applicablePromo = enabledPromos.find((promo: BundlingPromoItem) => promo.QUANTITY <= totalVideoCount);
    
    // If we found an applicable promo and this video is within the bundled quantity
    if (applicablePromo && index < applicablePromo.QUANTITY) {
      return applicablePromo.PRICE;
    } else {
      return defaultPrice;
    }
  }, [bundlingPromos, defaultPrice, paymentGatewayEnabled, effectiveVideoDetails.length]);

  // Calculate total amount with promo discount applied
  const calculateTotal = useCallback(() => {
    // If payment gateway is disabled or price is 0, total is 0
    if (!paymentGatewayEnabled || defaultPrice === 0) {
      return 0;
    }
    
    // Calculate base total with bundling prices
    let baseTotal = 0;
    
    // Apply bundling promo if available
    if (bundlingPromos && bundlingPromos.length > 0) {
      // Find the highest applicable bundling tier
      const enabledPromos = bundlingPromos
        .filter((promo: BundlingPromoItem) => promo.ENABLED && promo.QUANTITY > 0 && promo.PRICE >= 0)
        .sort((a: BundlingPromoItem, b: BundlingPromoItem) => b.QUANTITY - a.QUANTITY);
      
      // Find the applicable promo for the current video count
      const videoCount = effectiveVideoDetails.length;
      const applicablePromo = enabledPromos.find((promo: BundlingPromoItem) => promo.QUANTITY <= videoCount);
      
      if (applicablePromo) {
        const bundlingQuantity = applicablePromo.QUANTITY;
        const bundlingPrice = applicablePromo.PRICE;
        const regularVideos = videoCount - bundlingQuantity;
        
        // Calculate total with bundling
        baseTotal = (bundlingQuantity * bundlingPrice) + (regularVideos * defaultPrice);
      } else {
        // No applicable bundling, use default price for all
        baseTotal = videoCount * defaultPrice;
      }
    } else {
      // No bundling, use default price for all
      baseTotal = effectiveVideoDetails.length * defaultPrice;
    }
    
    // Apply promo code discount if available
    if (!validatedPromo) return baseTotal;
    
    if (validatedPromo.amount > 0) {
      // Fixed amount discount
      return Math.max(0, baseTotal - validatedPromo.amount);
    } else if (validatedPromo.percent > 0) {
      // Percentage discount
      const discount = (baseTotal * validatedPromo.percent) / 100;
      return Math.max(0, baseTotal - discount);
    }
    
    return baseTotal;
  }, [effectiveVideoDetails.length, defaultPrice, validatedPromo, bundlingPromos, paymentGatewayEnabled]);

  // Calculate discount amount separately for display
  const calculateDiscountAmount = useCallback(() => {
    if (!validatedPromo) return 0;
    
    // Calculate base total with bundling
    let baseTotal = 0;
    
    // Apply bundling promo if available
    if (bundlingPromos && bundlingPromos.length > 0) {
      // Find the highest applicable bundling tier
      const enabledPromos = bundlingPromos
        .filter((promo: BundlingPromoItem) => promo.ENABLED && promo.QUANTITY > 0 && promo.PRICE >= 0)
        .sort((a: BundlingPromoItem, b: BundlingPromoItem) => b.QUANTITY - a.QUANTITY);
      
      // Find the applicable promo for the current video count
      const videoCount = effectiveVideoDetails.length;
      const applicablePromo = enabledPromos.find((promo: BundlingPromoItem) => promo.QUANTITY <= videoCount);
      
      if (applicablePromo) {
        const bundlingQuantity = applicablePromo.QUANTITY;
        const bundlingPrice = applicablePromo.PRICE;
        const regularVideos = videoCount - bundlingQuantity;
        
        // Calculate total with bundling
        baseTotal = (bundlingQuantity * bundlingPrice) + (regularVideos * defaultPrice);
      } else {
        // No applicable bundling, use default price for all
        baseTotal = videoCount * defaultPrice;
      }
    } else {
      // No bundling, use default price for all
      baseTotal = effectiveVideoDetails.length * defaultPrice;
    }
    
    if (validatedPromo.amount > 0) {
      return validatedPromo.amount;
    } else if (validatedPromo.percent > 0) {
      return (baseTotal * validatedPromo.percent) / 100;
    }
    
    return 0;
  }, [effectiveVideoDetails.length, defaultPrice, validatedPromo, bundlingPromos]);

  // Update discount amount when validated promo changes
  useEffect(() => {
    if (validatedPromo) {
      setDiscountAmount(calculateDiscountAmount());
    } else {
      setDiscountAmount(0);
    }
  }, [validatedPromo, calculateDiscountAmount]);

  // Function to validate promo code
  const validatePromoCode = async () => {
    // Always reset the promo state when validating
    setValidatedPromo(null);
    setDiscountAmount(0);
    
    if (!promoCode.trim()) {
      setPromoError('Please enter a promo code');
      return;
    }
    
    try {
      setIsValidatingPromo(true);
      setPromoError(null);
      
      // Make the API call to validate the promo code
      const response = await validatePromo(promoCode.trim());
      
      if (response.valid) {
        setValidatedPromo(response.promo);
        // Discount amount will be set by the useEffect
      } else {
        // Display the specific error message from the server
        setPromoError(response.message || 'Invalid or expired promo code');
        setValidatedPromo(null);
      }
    } catch (err) {
      console.error('Promo Code Error:', err);
      setPromoError('Promo has ended or you entered a wrong code.');
      setValidatedPromo(null);
    } finally {
      setIsValidatingPromo(false);
    }
  };

  // Handle promo code input change
  const handlePromoCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPromoCode(e.target.value);
    // Only clear error if there is one
    if (promoError) setPromoError(null);
    
    // If the user completely clears the input, also clear the validated promo
    if (e.target.value.trim() === '') {
      setValidatedPromo(null);
      setDiscountAmount(0);
    }
  };

  // Modify the handleSubmit function to create transaction and redirect to Midtrans payment page
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault(); // Prevents default form submission but doesn't block input
    
    // Validate email
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      
      // Calculate total amount with possible promo code discount
      const totalAmount = calculateTotal();

      // Extract video IDs
      const videoIds = effectiveVideoDetails.map(video => video.id);
      
      // Create transaction with pending status
      const transactionResponse = await createTransaction({
        email,
        videoIds,
        totalAmount,
        venueId,
        fieldId,
        // Include promoId if we have a validated promo
        promoId: validatedPromo ? validatedPromo.id : undefined,
        // Also include the promo code to ensure backward compatibility
        promoCode: validatedPromo ? validatedPromo.code : undefined,
        // Add isStaging flag to indicate this is a staging transaction
        isStaging
      });
      
      console.log('Transaction created:', transactionResponse);
      
      // Start background processing for the videos
      // Pass transaction ID for later update
      processVideosInBackground(transactionResponse.transaction.id);
      
      // Clear cart and local storage
      localStorage.removeItem('clipo-selected-videos');
      localStorage.removeItem('clipo-video-cache');
      clearCart();
      
      // Check if payment gateway should be skipped
      if (transactionResponse.skip_payment_gateway) {
        console.log('Payment gateway is disabled. Skipping payment process.');
        setSuccessMessage('Your clipo is being prepared and will be sent to your email shortly.');
        setCountdown(10);
        return;
      }
      
      // Continue with payment process if payment gateway is enabled
      if (transactionResponse.payment && transactionResponse.payment.token) {
        // Load the Snap script if not already loaded
        try {
          // Get client key from environment
          const clientKey = process.env.REACT_APP_MIDTRANS_CLIENT_KEY;
          
          if (!clientKey) {
            console.error('REACT_APP_MIDTRANS_CLIENT_KEY is not set in environment variables');
            setError('Payment system is not properly configured. Please contact support.');
            setIsSubmitting(false);
            return;
          }
          
          await loadSnapScript(clientKey);
          
          // Show the payment container
          setShowPaymentForm(true);
          
          // Use setTimeout to ensure the container is rendered before embedding
          setTimeout(() => {
            // Ensure the container is properly rendered
            const snapContainer = document.getElementById('snap-container');
            if (!snapContainer) {
              console.error('Snap container not found');
              return;
            }
            
            console.log('Snap container found, embedding payment form');
            console.log('Container properties:', {
              width: snapContainer.offsetWidth,
              height: snapContainer.offsetHeight,
              visible: snapContainer.offsetParent !== null
            });
            
            // Embed Snap payment form
            console.log('Embedding Snap payment form with token:', transactionResponse.payment.token);
            try {
              window.snap.embed(transactionResponse.payment.token, {
                embedId: 'snap-container',
                onSuccess: function(result) {
                  console.log('Payment success:', result);
                  setShowPaymentForm(false);
                  setSuccessMessage('Payment completed! Your clipo will be sent to your email shortly.');
                  setCountdown(10);
                },
                onPending: function(result) {
                  console.log('Payment pending:', result);
                  setShowPaymentForm(false);
                  setSuccessMessage('Payment is pending. Your clipo will be sent to your email once payment is confirmed.');
                  setCountdown(10);
                },
                onError: function(result) {
                  console.error('Payment error:', result);
                  setShowPaymentForm(false);
                  setError('Payment failed: ' + (result?.message || 'Unknown error'));
                  setIsSubmitting(false);
                },
                onClose: function() {
                  console.log('Customer closed the payment form without finishing payment');
                  setShowPaymentForm(false);
                  setError('Payment cancelled. You can try again later.');
                  setIsSubmitting(false);
                }
              });
              
              // Add click event listeners to ensure iframe is clickable
              const snapIframe = document.querySelector('#snap-container iframe') as HTMLIFrameElement;
              if (snapIframe) {
                console.log('Found Snap iframe, ensuring it is clickable');
                snapIframe.style.pointerEvents = 'auto';
                snapIframe.style.zIndex = '10000';
              }
            } catch (err) {
              console.error('Error embedding Snap payment form:', err);
              setError('Failed to load payment form. Please try again later.');
              setIsSubmitting(false);
            }
          }, 500); // Increased timeout to ensure proper rendering
        } catch (error) {
          console.error('Error loading Snap:', error);
          setError('Failed to load payment system. Please try again later.');
          setIsSubmitting(false);
        }
      } else if (!transactionResponse.skip_payment_gateway) {
        // Only show error if payment was expected but not received
        console.error('No payment token received');
        setError('Payment initialization failed. Please try again later.');
        setIsSubmitting(false);
      }
      
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setIsSubmitting(false);
    }
  };

  // Update processVideosInBackground to use Google Drive upload
  const processVideosInBackground = async (transactionId: number) => {
    try {
      console.log(`Starting background video processing for transaction ${transactionId}`);
      
      // Set initial status
      setZipStatus('Preparing videos...');
      setZipProgress(10);

      // Upload videos to Google Drive or fallback to zip if needed
      let zipUrl: string | null = null;
      
      try {
        // First try the direct file access method
        try {
          // Get the actual file objects from the folder using the exact IDs from effectiveVideoDetails
          console.log(`Looking for ${effectiveVideoDetails.length} video files with IDs:`, effectiveVideoDetails.map(v => v.id));
          const videoFiles = await getVideoFiles(effectiveVideoDetails.map(video => video.id));
          
          // Check if we found at least some files
          if (videoFiles.length === 0) {
            console.warn('No video files found in folder, continuing without upload');
            throw new Error('No video files found');
          }
          
          console.log(`Found ${videoFiles.length} video files out of ${effectiveVideoDetails.length} requested`);
          console.log('Videos found for upload:', videoFiles.map(vf => vf.title));
          
          setZipStatus('Uploading videos...');
          setZipProgress(40);
          
          // Generate folder name with date and time
          const now = new Date();
          const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
          const timeStr = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
          const folderName = `videos_${dateStr}_${timeStr}_${videoFiles.length}videos`;
          console.log(`Uploading ${videoFiles.length} videos to Google Drive folder: ${folderName}`);
          
          // Upload videos to Google Drive
          zipUrl = await uploadVideosToGoogleDrive(videoFiles, folderName);
          
          // Check if upload was successful
          if (zipUrl) {
            console.log('Using Google Drive folder for videos:', zipUrl);
          } else {
            console.log('Google Drive upload failed, continuing without URL');
          }
          
        } catch (fileError) {
          console.error('Error with file-based upload:', fileError);
          throw fileError; // Re-throw the error instead of falling back to zip
        }
        
        // Update transaction with folder/zip URL
        setZipStatus('Finalizing transaction...');
        setZipProgress(90);
        
      } catch (uploadError) {
        console.error('Error uploading videos:', uploadError);
        zipUrl = null; // Set to null explicitly if failed
        setZipStatus('Completed (without upload)');
        setZipProgress(100);
      }
      
      try {
        // Make API call to update transaction with zipUrl (even if null)
        await api.updateTransactionZipUrl(transactionId, zipUrl);
        console.log('Transaction updated with zipUrl:', zipUrl);
        setZipStatus('Completed');
        setZipProgress(100);
      } catch (updateError) {
        console.error('Error updating transaction with zipUrl:', updateError);
        setZipStatus('Completed (update failed)');
        setZipProgress(100);
      }
      
    } catch (err) {
      console.error('Background processing error:', err);
      setZipStatus('Error processing videos');
      setZipProgress(100);
      
      // Notify server about the error
      try {
        await api.updateTransactionZipUrl(transactionId, null, true);
        console.log('Transaction marked as failed due to video upload error');
      } catch (updateError) {
        console.error('Failed to update transaction with error status:', updateError);
      }
    }
  };

  // Handle back button click - set flag to indicate returning from checkout
  const handleBackClick = () => {
    history.push('/box/video-selection');
  };

  const backgroundStyle = {
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.65)), url(${imageConfig.backgroundImage || imageConfig.defaultBackgroundImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '2rem',
    margin: 0,
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: -1
  };
  
  // Get video selection information for debugging
  useEffect(() => {
    if (effectiveVideoDetails.length > 0) {
      console.log(`CheckoutPage: Selected ${effectiveVideoDetails.length} videos for checkout`);
      
      // Log only the first video as an example
      if (effectiveVideoDetails[0]) {
        const video = effectiveVideoDetails[0];
        console.log(`Example - Video 1/${effectiveVideoDetails.length}:`, {
          title: video.title,
          hasBlob: video.url?.startsWith('blob:'),
          size: video.size || 'Unknown'
        });
      }
    }
  }, [effectiveVideoDetails]);

  // Update the custom styling for the payment container
  const paymentContainerStyle = {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    marginTop: '20px',
    width: '100%',
    maxWidth: '800px',
    margin: '30px auto',
    position: 'relative' as 'relative',
    zIndex: 999,
    pointerEvents: 'auto' as 'auto'
  };

  // Helper to format price for display
  const formatPrice = (price: number) => {
    if (price === 0) return 'Free';
    return `Rp ${price.toLocaleString('id-ID')}`;
  };

  return (
    <>
      <div className="checkout-page-background" style={backgroundStyle}></div>
      <div className="checkout-page">
        <div className="checkout-container">
          <h1>Complete Your Purchase</h1>
          
          {successMessage ? (
            <div className="success-message">
              <div className="success-icon">✓</div>
              <h2>Purchase Completed!</h2>
              <p>{successMessage}</p>
              <div className="spam-warning">
                <p><strong>⚠️ Please check your spam folder if you don't see our email!</strong></p>
              </div>
              <p className="redirect-message">
                You will be redirected to the home page in {countdown} second{countdown !== 1 ? 's' : ''}...
              </p>
            </div>
          ) : (
            <>
              <div className="order-summary">
                <h2>Order Summary</h2>
                <ul className="video-list">
                  {effectiveVideoDetails.map((video, index) => (
                    <li key={video.id} className="video-item">
                      <div className="video-thumbnail">
                        <img src={video.thumbnailUrl || '/assets/default-thumbnail.jpg'} alt={video.title} />
                      </div>
                      <div className="video-details">
                        <span className="video-title">{video.title}</span>
                        <span className="video-price">
                          {!paymentGatewayEnabled || defaultPrice === 0 
                            ? 'Free' 
                            : `Rp ${calculateItemPrice(index).toLocaleString('id-ID')}`}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
                
                {/* Promo Code Form - only show if payment gateway is enabled and price is not 0 */}
                {paymentGatewayEnabled && defaultPrice > 0 && (
                <div className="promo-code-section">
                  <div className="promo-code-form">
                    <input
                      type="text"
                      placeholder="Enter promo code"
                      value={promoCode}
                      onChange={handlePromoCodeChange}
                      disabled={isValidatingPromo}
                      className="promo-code-input"
                    />
                    <button
                      type="button"
                      onClick={validatePromoCode}
                      disabled={isValidatingPromo || !promoCode.trim()}
                      className="promo-code-button"
                    >
                      {isValidatingPromo ? 'Validating...' : validatedPromo ? 'Reapply' : 'Apply'}
                    </button>
                    
                    {validatedPromo && (
                      <button
                        type="button"
                        onClick={() => {
                          setValidatedPromo(null);
                          setPromoCode('');
                          setDiscountAmount(0);
                        }}
                        className="promo-code-remove"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  {promoError && <div className="promo-error-message">{promoError}</div>}
                  
                  {validatedPromo && (
                    <div className="promo-applied">
                      <span className="promo-success">✓ {validatedPromo.name} applied!</span>
                      <span className="promo-discount">
                        {validatedPromo.percent > 0 
                          ? `${validatedPromo.percent}% off` 
                          : `Rp ${validatedPromo.amount.toLocaleString('id-ID')} off`}
                      </span>
                    </div>
                  )}
                </div>
                )}
                
                <div className="order-total">
                  {paymentGatewayEnabled && defaultPrice > 0 ? (
                    <>
                  <div className="subtotal">
                    <span>Subtotal:</span>
                        <span>
                          {bundlingPromos && bundlingPromos.length > 0 ? (
                            (() => {
                              // Find the highest applicable bundling tier
                              const enabledPromos = bundlingPromos
                                .filter((promo: BundlingPromoItem) => promo.ENABLED && promo.QUANTITY > 0 && promo.PRICE >= 0)
                                .sort((a: BundlingPromoItem, b: BundlingPromoItem) => b.QUANTITY - a.QUANTITY);
                              
                              // Find the applicable promo for the current video count
                              const videoCount = effectiveVideoDetails.length;
                              const applicablePromo = enabledPromos.find(promo => promo.QUANTITY <= videoCount);
                              
                              if (applicablePromo) {
                                const bundlingQuantity = applicablePromo.QUANTITY;
                                const bundlingPrice = applicablePromo.PRICE;
                                const regularVideos = videoCount - bundlingQuantity;
                                
                                return (
                                  <>
                                    {bundlingQuantity} × {formatPrice(bundlingPrice)}
                                    {regularVideos > 0 && (
                                      <> + {regularVideos} × {formatPrice(defaultPrice)}</>
                                    )}
                                  </>
                                );
                              } else {
                                return `${effectiveVideoDetails.length} × ${formatPrice(defaultPrice)}`;
                              }
                            })()
                          ) : (
                            `${effectiveVideoDetails.length} × ${formatPrice(defaultPrice)}`
                          )}
                        </span>
                  </div>
                  
                  {discountAmount > 0 && (
                    <div className="discount">
                      <span>Discount:</span>
                      <span>-Rp {discountAmount.toLocaleString('id-ID')}</span>
                    </div>
                  )}
                  
                  <div className="final-total">
                    <span>Total:</span>
                    <span className="total-price">Rp {calculateTotal().toLocaleString('id-ID')}</span>
                  </div>
                    </>
                  ) : (
                    <div className="final-total">
                      <span>Total:</span>
                      <span className="total-price">Free</span>
                    </div>
                  )}
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="checkout-form">
                <div className="form-group">
                  <label htmlFor="email">Email Address:</label>
                  <EmailInput
                    value={email}
                    onChange={handleEmailChange}
                    inputRef={emailInputRef}
                    placeholder="Enter your email address"
                    autoComplete="off"
                  />
                  {error && <div className="error-message">{error}</div>}
                </div>
                
                <div className="form-actions">
                  <button 
                    type="button"
                    onClick={handleBackClick}
                    className="back-button"
                  >
                    Back
                  </button>
                  <button 
                    type="submit" 
                    className="checkout-button"
                    disabled={isSubmitting}
                  >
                    Pay Now
                  </button>
                </div>
              </form>
            </>
          )}
        </div>

        {/* Move payment form outside the checkout container */}
        {showPaymentForm && !successMessage && (
          <div 
            className="payment-container" 
            style={paymentContainerStyle}
          >
            <h3>Select Payment Method</h3>
            <div 
              id="snap-container" 
              style={{ 
                width: '100%', 
                height: '700px',
                position: 'relative',
                zIndex: 9999,
                pointerEvents: 'auto',
                overflow: 'visible',
                backgroundColor: 'white',
                display: 'block',
                touchAction: 'auto',
                marginTop: '20px'
              }}
              onClick={(e) => {
                e.stopPropagation();
                console.log('Snap container clicked');
              }}
            ></div>
          </div>
        )}
      </div>
    </>
  );
};

export default CheckoutPage;